import { useState, useEffect } from "react";
import { Calculator, ArrowRightLeft, AlertTriangle, Check, Info } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);

const SteuervergleichTab = ({ unternehmensform, land }) => {
  const isAT = land === "AT";
  const [steuerVergleich, setSteuerVergleich] = useState(null);
  const [steuerEingaben, setSteuerEingaben] = useState({
    jahresumsatz: "",
    wareneinsatz_prozent: "30",
    fixkosten_jahr: "",
    personalkosten_jahr: "",
    hebesatz_gewerbesteuer: "400"
  });
  const [vergleichLoading, setVergleichLoading] = useState(false);

  const handleSteuerVergleich = async () => {
    if (!steuerEingaben.jahresumsatz) {
      toast.error("Bitte Jahresumsatz eingeben");
      return;
    }
    setVergleichLoading(true);
    try {
      const response = await axios.post(`${API}/tools/unternehmensformen-vergleich`, {
        unternehmensform: unternehmensform || (isAT ? "AT_EU" : "EU"),
        jahresumsatz: parseFloat(steuerEingaben.jahresumsatz),
        wareneinsatz_prozent: parseFloat(steuerEingaben.wareneinsatz_prozent) || 30,
        fixkosten_jahr: parseFloat(steuerEingaben.fixkosten_jahr) || 0,
        personalkosten_jahr: parseFloat(steuerEingaben.personalkosten_jahr) || 0,
        hebesatz_gewerbesteuer: parseFloat(steuerEingaben.hebesatz_gewerbesteuer) || 400
      }, { withCredentials: true });
      setSteuerVergleich(response.data);
    } catch (error) {
      toast.error("Fehler bei Steuerberechnung");
    } finally {
      setVergleichLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="steuervergleich-tab">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="font-semibold text-white mb-1 flex items-center gap-2">
          <Calculator size={18} className="text-amber-400" />
          {isAT ? "Unternehmensformen-Vergleich (Steuerrecht AT 2024/2025)" : "Unternehmensformen-Vergleich (Steuerrecht DE 2025)"}
        </h3>
        <p className="text-xs text-slate-400 mb-4">
          {isAT
            ? "ESt §33 EStG AT (7 Stufen 0-55%), KoeSt §22 KStG 23%, Kommunalsteuer 3% KommStG"
            : "ESt-Tarif §32a EStG 2025, GewSt §7-11 GewStG, KSt §23 KStG 15%, Soli §3-4 SolZG"
          }
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-400">Jahresumsatz (EUR) *</Label>
            <Input type="number" value={steuerEingaben.jahresumsatz} onChange={(e) => setSteuerEingaben({...steuerEingaben, jahresumsatz: e.target.value})} placeholder="z.B. 145000" className="bg-slate-700 border-slate-600" data-testid="input-jahresumsatz" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-400">Wareneinsatz (%)</Label>
            <Input type="number" value={steuerEingaben.wareneinsatz_prozent} onChange={(e) => setSteuerEingaben({...steuerEingaben, wareneinsatz_prozent: e.target.value})} placeholder="30" className="bg-slate-700 border-slate-600" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-400">Fixkosten/Jahr (EUR)</Label>
            <Input type="number" value={steuerEingaben.fixkosten_jahr} onChange={(e) => setSteuerEingaben({...steuerEingaben, fixkosten_jahr: e.target.value})} placeholder="z.B. 41964" className="bg-slate-700 border-slate-600" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-400">Personalkosten/Jahr (EUR)</Label>
            <Input type="number" value={steuerEingaben.personalkosten_jahr} onChange={(e) => setSteuerEingaben({...steuerEingaben, personalkosten_jahr: e.target.value})} placeholder="z.B. 22464" className="bg-slate-700 border-slate-600" />
          </div>
          {!isAT && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-400">Gewerbesteuer-Hebesatz (%)</Label>
              <Input type="number" value={steuerEingaben.hebesatz_gewerbesteuer} onChange={(e) => setSteuerEingaben({...steuerEingaben, hebesatz_gewerbesteuer: e.target.value})} placeholder="400" className="bg-slate-700 border-slate-600" data-testid="input-hebesatz" />
              <p className="text-[10px] text-slate-500">Limburg: 400%, Frankfurt: 460%, Muenchen: 490%</p>
            </div>
          )}
        </div>

        <Button onClick={handleSteuerVergleich} disabled={vergleichLoading} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-steuervergleich">
          <ArrowRightLeft size={16} className="mr-2" />
          {vergleichLoading ? "Berechne..." : "Unternehmensformen vergleichen"}
        </Button>
      </div>

      {steuerVergleich && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-400">Gewinn vor Steuern</p>
              <p className="text-xl font-bold text-white">{formatCurrency(steuerVergleich.eingaben.gewinn_vor_steuern)}</p>
              <p className="text-xs text-slate-500 mt-1">Land: {steuerVergleich.land === "AT" ? "Oesterreich" : "Deutschland"}</p>
            </div>
            {steuerVergleich.empfehlung?.beste_wahl && (
              <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Check size={16} className="text-emerald-400" />
                  <p className="text-xs text-emerald-400 font-semibold">OPTIMALE RECHTSFORM</p>
                </div>
                <p className="text-lg font-bold text-white">{steuerVergleich.empfehlung.name}</p>
                <p className="text-xs text-emerald-300 mt-1">{steuerVergleich.empfehlung.begruendung}</p>
              </div>
            )}
          </div>

          {/* Tabelle */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="text-left p-3 font-semibold text-slate-300">Rechtsform</th>
                    {!isAT && <th className="text-right p-3 font-semibold text-slate-300">GewSt</th>}
                    <th className="text-right p-3 font-semibold text-slate-300">{isAT ? "ESt/KoeSt" : "ESt/KSt"}</th>
                    {isAT && <th className="text-right p-3 font-semibold text-slate-300">KommSt</th>}
                    {!isAT && <th className="text-right p-3 font-semibold text-slate-300">Soli</th>}
                    <th className="text-right p-3 font-semibold text-slate-300">Steuern ges.</th>
                    <th className="text-right p-3 font-semibold text-slate-300">Quote</th>
                    <th className="text-right p-3 font-semibold text-emerald-400 bg-emerald-900/10">Netto/Monat</th>
                    <th className="text-right p-3 font-semibold text-emerald-400 bg-emerald-900/10">Netto/Jahr</th>
                  </tr>
                </thead>
                <tbody>
                  {steuerVergleich.vergleich.map((uf, index) => (
                    <tr key={uf.code} className={`border-t border-slate-700/50 ${index === 0 ? 'bg-emerald-900/10' : ''} ${uf.code === unternehmensform ? 'ring-2 ring-violet-500 ring-inset' : ''}`}>
                      <td className="p-3">
                        <div className="font-semibold text-white">{uf.name}</div>
                        <div className="text-[10px] text-slate-500">
                          {uf.haftung?.includes('Unbeschraenkt') || uf.haftung?.includes('Unbeschränkt') ? 'Unbeschraenkt' : 'Beschraenkt'}
                          {uf.gewerbesteuer_freibetrag > 0 && ` | FB ${formatCurrency(uf.gewerbesteuer_freibetrag)}`}
                          {uf.mindestkapital && ` | MK ${formatCurrency(uf.mindestkapital)}`}
                        </div>
                        {index === 0 && <span className="text-[9px] text-emerald-400 font-semibold">BESTE WAHL</span>}
                      </td>
                      {!isAT && <td className="p-3 text-right text-red-400 font-mono">{formatCurrency(uf.gewerbesteuer)}</td>}
                      <td className="p-3 text-right text-red-400 font-mono">
                        {formatCurrency(uf.einkommensteuer + (uf.koerperschaftsteuer || 0))}
                        {uf.est_anrechnung_gewst > 0 && (
                          <div className="text-[9px] text-emerald-400">-{formatCurrency(uf.est_anrechnung_gewst)} §35</div>
                        )}
                      </td>
                      {isAT && <td className="p-3 text-right text-orange-400 font-mono">{formatCurrency(uf.kommunalsteuer)}</td>}
                      {!isAT && <td className="p-3 text-right text-red-400 font-mono">{formatCurrency(uf.solidaritaetszuschlag)}</td>}
                      <td className="p-3 text-right text-red-400 font-mono font-semibold">
                        {formatCurrency(uf.steuern_gesamt)}
                        {uf.ruecklage > 0 && <div className="text-[9px] text-amber-400">+{formatCurrency(uf.ruecklage)} Ruecklage</div>}
                      </td>
                      <td className="p-3 text-right text-slate-400">{uf.effektive_steuerquote}%</td>
                      <td className="p-3 text-right font-bold text-emerald-400 bg-emerald-900/10 font-mono">{formatCurrency(uf.netto_monat)}</td>
                      <td className="p-3 text-right font-bold text-emerald-400 bg-emerald-900/10 font-mono">{formatCurrency(uf.netto_jahr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top 3 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {steuerVergleich.vergleich.slice(0, 3).map((uf, index) => (
              <div key={uf.code} className={`border rounded-xl p-4 ${index === 0 ? 'bg-emerald-900/10 border-emerald-700/50' : 'bg-slate-800/50 border-slate-700'}`}>
                <h4 className="font-semibold text-sm text-white mb-1">{index + 1}. {uf.name}</h4>
                <p className="text-xl font-bold text-emerald-400 mb-3">{formatCurrency(uf.netto_monat)}/Monat</p>
                {uf.vorteile?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[10px] uppercase text-emerald-400 font-semibold mb-1">Vorteile</p>
                    <ul className="text-xs text-slate-300 space-y-0.5">
                      {uf.vorteile.map((v, i) => (
                        <li key={i} className="flex items-start gap-1"><Check size={10} className="text-emerald-500 mt-0.5 flex-shrink-0" /> {v}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {uf.nachteile?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase text-red-400 font-semibold mb-1">Nachteile</p>
                    <ul className="text-xs text-slate-300 space-y-0.5">
                      {uf.nachteile.map((n, i) => (
                        <li key={i} className="flex items-start gap-1"><AlertTriangle size={10} className="text-amber-500 mt-0.5 flex-shrink-0" /> {n}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h4 className="text-xs font-semibold text-slate-300 mb-2 flex items-center gap-1">
              <Info size={12} /> Rechtsgrundlagen ({steuerVergleich.land === "AT" ? "Oesterreich" : "Deutschland"})
            </h4>
            <p className="text-[11px] text-slate-400 leading-relaxed">{steuerVergleich.rechtsgrundlagen}</p>
          </div>

          <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg text-xs text-amber-300">
            <AlertTriangle size={14} className="inline mr-1" />
            <strong>Hinweis:</strong> {steuerVergleich.hinweis}
          </div>
        </div>
      )}
    </div>
  );
};

export default SteuervergleichTab;
